import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  FileText, 
  Download,
  Filter,
  BarChart3,
  PieChart,
  RefreshCw
} from 'lucide-react';

interface SalesData {
  date: string;
  invoices: number;
  revenue: number;
  paid: number;
  unpaid: number;
  cancelled: number;
}

interface SalesReportsProps {
  invoices: any[];
}

export const SalesReports: React.FC<SalesReportsProps> = ({ invoices }) => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [salesData, setSalesData] = useState<SalesData[]>([]);

  useEffect(() => {
    generateSalesData();
  }, [dateRange, reportType, invoices]);

  const generateSalesData = () => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const data: SalesData[] = [];

    // Filter invoices by date range
    const filteredInvoices = invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.invoice_date);
      return invoiceDate >= start && invoiceDate <= end;
    });

    // Generate data points based on report type
    const current = new Date(start);
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      
      // Get invoices for this date
      const dayInvoices = filteredInvoices.filter(invoice => 
        invoice.invoice_date === dateStr
      );

      const dayData: SalesData = {
        date: dateStr,
        invoices: dayInvoices.length,
        revenue: dayInvoices.reduce((sum, inv) => sum + inv.total_amount, 0),
        paid: dayInvoices.filter(inv => inv.payment_status === 'paid').length,
        unpaid: dayInvoices.filter(inv => inv.payment_status === 'unpaid').length,
        cancelled: dayInvoices.filter(inv => inv.payment_status === 'cancelled').length
      };

      data.push(dayData);

      // Increment date based on report type
      if (reportType === 'daily') {
        current.setDate(current.getDate() + 1);
      } else if (reportType === 'weekly') {
        current.setDate(current.getDate() + 7);
      } else {
        current.setMonth(current.getMonth() + 1);
      }
    }

    setSalesData(data);
  };

  const exportReport = () => {
    const csvContent = [
      ['Date', 'Invoices', 'Revenue (€)', 'Paid', 'Unpaid', 'Cancelled'].join(','),
      ...salesData.map(data => [
        data.date,
        data.invoices,
        data.revenue.toFixed(2),
        data.paid,
        data.unpaid,
        data.cancelled
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sales_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const totals = salesData.reduce((acc, data) => ({
    invoices: acc.invoices + data.invoices,
    revenue: acc.revenue + data.revenue,
    paid: acc.paid + data.paid,
    unpaid: acc.unpaid + data.unpaid,
    cancelled: acc.cancelled + data.cancelled
  }), { invoices: 0, revenue: 0, paid: 0, unpaid: 0, cancelled: 0 });

  return (
    <div className="space-y-6">
      {/* Report Controls */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2" />
          📊 Sales Reports & Analytics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <Input
            label="Start Date"
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
          />
          
          <Input
            label="End Date"
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as any)}
              className="input"
            >
              <option value="daily">📅 Daily</option>
              <option value="weekly">📊 Weekly</option>
              <option value="monthly">🗓️ Monthly</option>
            </select>
          </div>

          <div className="flex items-end">
            <Button onClick={exportReport} variant="secondary" className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card p-4 bg-blue-50 dark:bg-blue-900/20">
          <div className="text-2xl font-bold text-blue-600">{totals.invoices}</div>
          <div className="text-sm text-blue-700 dark:text-blue-300">Total Invoices</div>
        </div>
        
        <div className="card p-4 bg-green-50 dark:bg-green-900/20">
          <div className="text-2xl font-bold text-green-600">€{totals.revenue.toFixed(2)}</div>
          <div className="text-sm text-green-700 dark:text-green-300">Total Revenue</div>
        </div>
        
        <div className="card p-4 bg-emerald-50 dark:bg-emerald-900/20">
          <div className="text-2xl font-bold text-emerald-600">{totals.paid}</div>
          <div className="text-sm text-emerald-700 dark:text-emerald-300">✅ Paid</div>
        </div>
        
        <div className="card p-4 bg-red-50 dark:bg-red-900/20">
          <div className="text-2xl font-bold text-red-600">{totals.unpaid}</div>
          <div className="text-sm text-red-700 dark:text-red-300">❌ Unpaid</div>
        </div>
        
        <div className="card p-4 bg-orange-50 dark:bg-orange-900/20">
          <div className="text-2xl font-bold text-orange-600">{totals.cancelled}</div>
          <div className="text-sm text-orange-700 dark:text-orange-300">🚫 Cancelled</div>
        </div>
      </div>

      {/* Sales Chart */}
      <div className="card p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          📈 Revenue Trend
        </h4>
        
        <div className="space-y-3">
          {salesData.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No sales data for selected period
            </div>
          ) : (
            <div className="space-y-2">
              {salesData.map((data, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {new Date(data.date).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {data.invoices} invoices
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-sm font-medium text-green-600 dark:text-green-400">
                      €{data.revenue.toFixed(2)}
                    </div>
                    <div className="flex space-x-2 text-xs">
                      <span className="text-green-600">✅{data.paid}</span>
                      <span className="text-red-600">❌{data.unpaid}</span>
                      {data.cancelled > 0 && (
                        <span className="text-orange-600">🚫{data.cancelled}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};